import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, MenuItem, Select, TableCell, TableRow, TextField } from '@mui/material';
import { FileUpload } from 'components/FileUpload';
import { Delete, Check } from '@mui/icons-material';
import { RequiredStar } from 'components/RequiredStarSpan';
import { IProcurement } from 'types/procurements';
import { EBidProcurementMeasurement } from 'types/bids/bidProcurementItems';

const capitalize: Function = (text: string) => {
  return text.charAt(0).toLocaleUpperCase('az') + text.slice(1).toLocaleLowerCase()
}

interface FormData extends IProcurement {
}

export function AddWorkRateTableRow({ onClose, no, onAdd, disabled }: {
  onClose: () => void;
  no: number;
  onAdd: (workRate: IProcurement) => void;
  disabled: boolean
}) {
  const { t } = useTranslation()

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    control,
  } = useForm<FormData>({
    reValidateMode: 'onSubmit',
    defaultValues: {},
  })

  const onSubmit = (data: FormData) => {
    // TODO: add API call then call onAdd with received data
    onAdd(data)
    onClose()
  }

  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell>
        {no}
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          label={
            <>
              {t('name')} <RequiredStar>*</RequiredStar>
            </>
          }
          variant="outlined"
          error={!!errors.name}
          {...register('name', { required: t('required') })}
        />
      </TableCell>
      <TableCell>
        <TextField
          label={
            <>
              {t('description')} <RequiredStar>*</RequiredStar>
            </>
          }
          variant="outlined"
          multiline
          size="small"
          error={!!errors.description}
          {...register('description', { required: t('required') })}
        />
      </TableCell>
      <TableCell>
        <TextField
          label={
            <>
              {t('quantity')} <RequiredStar>*</RequiredStar>
            </>
          }
          variant="outlined"
          size="small"
          error={!!errors.quantity}
          {...register('quantity', { required: t('required') })}
        />
      </TableCell>
      <TableCell>
        <Controller
          name="measurement"
          control={control}
          rules={{ required: t('required') }}
          render={({ field }) => (
            <Select
              {...field}
              sx={{ width: '100%' }}
              error={!!errors.measurement}
              size="small"
              labelId="measurement-select-helper-label"
              label={
                <>
                  {t('measurement')} <RequiredStar>*</RequiredStar>
                </>
              }
            >
              {Object.keys(EBidProcurementMeasurement).map((measurement) => (
                <MenuItem value={measurement} key={measurement}>
                  {capitalize(t(measurement))}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </TableCell>
      <TableCell sx={{ width: '240px' }}>
        <Controller
          name="attachedFileId"
          control={control}
          render={({ field }) => (
            <FileUpload
              text={t('extra-document')}
              handleFile={(file) => {
                field.onChange(file.id)
              }}
              fileId={field.value}
              accept="image/png, image/gif, image/jpeg"
              onDelete={() => setValue('attachedFileId', undefined)}
            />
          )}
        />
      </TableCell>
      <TableCell padding="checkbox">
        <Box display="flex" alignItems="center">
          <IconButton disabled={disabled} onClick={handleSubmit(onSubmit)}>
            <Check color="primary" fontSize="small" />
          </IconButton>
          <IconButton onClick={onClose}>
            <Delete color="error" fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  )
}
